import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { useAuth } from './AuthContext';

// ─── Constants ────────────────────────────────────────────────────────────────

export const MAX_EXERCISE_ENERGY = 150;
export const MAX_SLEEP_ENERGY = 100;
export const MAX_HYDRATION_ENERGY = 75;
export const MAX_STEPS_ENERGY = 75;
export const MAX_TOTAL_ENERGY = 400;
export const PERFECT_DAY_THRESHOLD = 380;
export const PERFECT_DAY_BONUS_XP = 50;

// Default weekly workout target (a goal, not an activity stat)
export const WEEKLY_WORKOUT_GOAL = 5;

export const INTENSITY_FACTORS = {
  low: 0.6,
  moderate: 1.0,
  high: 1.4,
  extreme: 1.8,
};

export const SLEEP_CURVE = [
  { maxHours: 4, points: 10 },
  { maxHours: 5, points: 30 },
  { maxHours: 6, points: 50 },
  { maxHours: 7, points: 70 },
  { maxHours: 8, points: 90 },
  { maxHours: 9, points: 100 }, // optimal
  { maxHours: 10, points: 85 },
  { maxHours: Infinity, points: 40 }, // oversleeping
];

export const COMPANION_LEVELS = [
  { level: 1, name: 'Stage 1', xpRequired: 0 },
  { level: 2, name: 'Stage 2', xpRequired: 2800 },
  { level: 3, name: 'Stage 3', xpRequired: 8400 },
  { level: 4, name: 'Stage 4', xpRequired: 25000 },
  { level: 5, name: 'Stage 5', xpRequired: 50000 },
  { level: 6, name: 'Stage 6', xpRequired: 100000 },
  { level: 7, name: 'Stage 7', xpRequired: 200000 },
];

export const DRAGON_LEVELS = COMPANION_LEVELS;

export const RECOVERY_STATUS = {
  optimal: { label: 'optimal', minScore: 85 },
  good: { label: 'good', minScore: 65 },
  moderate: { label: 'moderate', minScore: 40 },
  low: { label: 'low', minScore: 0 },
};

// Default weight when no profile weight is available (kg)
const DEFAULT_WEIGHT_KG = 70;

// localStorage keys
const STORAGE_KEYS = {
  energy: 'omnisio_workout_energy',
  companion: 'omnisio_workout_companion',
  dragon: 'omnisio_workout_dragon', // legacy key — kept for migration
  streak: 'omnisio_workout_streak',
  weekly: 'omnisio_workout_weekly',
  profile: 'omnisio_workout_profile',
  // Legacy key for migration
  legacy: 'omnisio_workout_energy',
};

// ─── Pure Calculation Functions ───────────────────────────────────────────────

export function calcStreakMultiplier(days) {
  if (days >= 31) return 1.5;
  if (days >= 15) return 1.3;
  if (days >= 8) return 1.2;
  if (days >= 4) return 1.1;
  return 1.0;
}

export function calcExerciseEnergy(durationMin, intensity, reps, streakMultiplier) {
  const factor = INTENSITY_FACTORS[intensity] ?? INTENSITY_FACTORS.moderate;
  const baseEnergy = durationMin * factor * 2.5;
  const repBonus = (reps ?? 0) * 0.5;
  return Math.min(MAX_EXERCISE_ENERGY, (baseEnergy + repBonus) * streakMultiplier);
}

export function calcSleepEnergy(hours) {
  for (const tier of SLEEP_CURVE) {
    if (hours <= tier.maxHours) return tier.points;
  }
  return SLEEP_CURVE[SLEEP_CURVE.length - 1].points;
}

export function calcHydrationEnergy(loggedMl, weightKg) {
  const targetMl = weightKg * 35;
  if (targetMl <= 0) return 0;
  return Math.min(MAX_HYDRATION_ENERGY, (loggedMl / targetMl) * MAX_HYDRATION_ENERGY);
}

export function calcStepsEnergy(steps) {
  if (steps <= 10000) {
    return (steps / 10000) * 70;
  }
  return 70 + Math.min(5, ((steps - 10000) / 2000) * 5);
}

export function calcRecoveryScore(sleepEnergy, hydrationEnergy) {
  return (sleepEnergy / MAX_SLEEP_ENERGY) * 60 + (hydrationEnergy / MAX_HYDRATION_ENERGY) * 40;
}

export function getRecoveryStatus(score) {
  if (score >= RECOVERY_STATUS.optimal.minScore) return 'optimal';
  if (score >= RECOVERY_STATUS.good.minScore) return 'good';
  if (score >= RECOVERY_STATUS.moderate.minScore) return 'moderate';
  return 'low';
}

export function getDragonInfo(totalXP) {
  let current = DRAGON_LEVELS[0];
  let next = DRAGON_LEVELS[1] ?? null;

  for (let i = DRAGON_LEVELS.length - 1; i >= 0; i--) {
    if (totalXP >= DRAGON_LEVELS[i].xpRequired) {
      current = DRAGON_LEVELS[i];
      next = DRAGON_LEVELS[i + 1] ?? null;
      break;
    }
  }

  const xpIntoLevel = totalXP - current.xpRequired;
  const xpNeeded = next ? next.xpRequired - current.xpRequired : 1;
  const xpProgress = next ? Math.min(1, xpIntoLevel / xpNeeded) : 1;
  const xpToNextLevel = next ? next.xpRequired - totalXP : 0;

  return {
    dragonLevel: current.level,
    dragonName: current.name,
    xpToNextLevel,
    xpProgress,
  };
}

// ─── Storage Helpers ──────────────────────────────────────────────────────────

function getTodayKey() {
  return new Date().toISOString().slice(0, 10); // YYYY-MM-DD
}

function loadJSON(key) {
  try {
    const raw = localStorage.getItem(key);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function saveJSON(key, value) {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // Storage quota exceeded or unavailable — fail silently
  }
}

function loadDailyEnergy() {
  const data = loadJSON(STORAGE_KEYS.energy);
  if (data && data.date === getTodayKey()) return data;

  // Attempt legacy migration from old energym key
  const legacy = loadJSON(STORAGE_KEYS.legacy);
  if (legacy && legacy.date === getTodayKey()) {
    // Map legacy exercise energy if present, cap at new max
    return {
      date: getTodayKey(),
      exerciseEnergy: Math.min(MAX_EXERCISE_ENERGY, legacy.exerciseEnergy ?? legacy.energy ?? 0),
      sleepEnergy: 0,
      hydrationEnergy: 0,
      stepsEnergy: 0,
      waterLogged: 0,
      stepsCount: 0,
    };
  }

  return null;
}

function saveDailyEnergy(state) {
  saveJSON(STORAGE_KEYS.energy, { ...state, date: getTodayKey() });
}

function loadDragon() {
  // Check new key first; fall back to legacy 'omnisio_workout_dragon' for migration
  const fromNew = loadJSON(STORAGE_KEYS.companion);
  if (fromNew) return fromNew;
  const fromLegacy = loadJSON(STORAGE_KEYS.dragon);
  if (fromLegacy) {
    // Migrate to new key
    saveJSON(STORAGE_KEYS.companion, fromLegacy);
    return fromLegacy;
  }
  return { dragonXP: 0 };
}

function saveDragon(state) {
  saveJSON(STORAGE_KEYS.companion, state);
}

function loadStreak() {
  return loadJSON(STORAGE_KEYS.streak) ?? { count: 0, lastActiveDate: null };
}

function saveStreak(state) {
  saveJSON(STORAGE_KEYS.streak, state);
}

// Reads the number of workouts logged in the current week.
// Honest source: the persisted weekly counter if present, otherwise 0.
// Supports both a bare number and a { count } / { workouts } shape.
function loadWeeklyWorkouts() {
  const data = loadJSON(STORAGE_KEYS.weekly);
  if (typeof data === 'number' && Number.isFinite(data)) return Math.max(0, data);
  if (data && typeof data === 'object') {
    const n = data.count ?? data.workouts ?? 0;
    return Number.isFinite(n) ? Math.max(0, n) : 0;
  }
  return 0;
}

function loadUserProfile() {
  return loadJSON(STORAGE_KEYS.profile) ?? { weightKg: DEFAULT_WEIGHT_KG };
}

function saveUserProfile(state) {
  saveJSON(STORAGE_KEYS.profile, state);
}

// ─── Context ──────────────────────────────────────────────────────────────────

const EnergyContext = createContext(null);

export function EnergyProvider({ children }) {
  const { profile, updateProfile } = useAuth();

  // Resolve weight from auth profile or local cache
  const resolveWeight = useCallback(() => {
    if (profile?.weight_kg && profile.weight_kg > 0) return profile.weight_kg;
    return loadUserProfile().weightKg ?? DEFAULT_WEIGHT_KG;
  }, [profile]);

  // ── Daily energy state ─────────────────────────────────────────────────────
  const [dailyEnergy, setDailyEnergy] = useState(() => {
    const saved = loadDailyEnergy();
    return saved ?? {
      exerciseEnergy: 0,
      sleepEnergy: 0,
      hydrationEnergy: 0,
      stepsEnergy: 0,
      waterLogged: 0,
      stepsCount: 0,
    };
  });

  // ── Dragon state (persistent) ──────────────────────────────────────────────
  const [dragonXP, setDragonXP] = useState(() => loadDragon().dragonXP ?? 0);

  // ── Streak state (persistent) ──────────────────────────────────────────────
  const [streakData, setStreakData] = useState(() => {
    const saved = loadStreak();
    // Also accept legacy streak from auth profile
    const count = saved.count || profile?.streak || 0;
    return { count, lastActiveDate: saved.lastActiveDate ?? null };
  });

  // ── #14 isolation: re-initialize in-memory state when the user changes ──────
  // AuthContext clears the previous user's localStorage on user change, but this
  // provider's state was seeded once at mount. If a different user becomes active
  // without a remount, reset energy/dragon/streak from the (now-cleared) cache so
  // no previous-user data leaks. Keyed on a stable per-user id.
  const userKey =
    profile?.public_id || profile?._id || profile?.id || profile?.auth_id ||
    profile?.telegram_id || profile?.email || null;
  const prevUserKeyRef = React.useRef(userKey);
  useEffect(() => {
    if (prevUserKeyRef.current === userKey) return;
    prevUserKeyRef.current = userKey;
    // Re-hydrate from localStorage (AuthContext already wiped the old user's keys).
    const savedEnergy = loadDailyEnergy();
    setDailyEnergy(savedEnergy ?? {
      exerciseEnergy: 0,
      sleepEnergy: 0,
      hydrationEnergy: 0,
      stepsEnergy: 0,
      waterLogged: 0,
      stepsCount: 0,
    });
    setDragonXP(loadDragon().dragonXP ?? 0);
    const savedStreak = loadStreak();
    setStreakData({
      count: savedStreak.count || profile?.streak || 0,
      lastActiveDate: savedStreak.lastActiveDate ?? null,
    });
  }, [userKey, profile?.streak]);

  // ── Persist on change ──────────────────────────────────────────────────────
  useEffect(() => {
    saveDailyEnergy(dailyEnergy);
  }, [dailyEnergy]);

  useEffect(() => {
    saveDragon({ dragonXP });
  }, [dragonXP]);

  useEffect(() => {
    saveStreak(streakData);
  }, [streakData]);

  // ── Midnight reset check ───────────────────────────────────────────────────
  useEffect(() => {
    const resetIfNewDay = () => {
      const saved = loadJSON(STORAGE_KEYS.energy);
      if (!saved || saved.date !== getTodayKey()) {
        setDailyEnergy({
          exerciseEnergy: 0,
          sleepEnergy: 0,
          hydrationEnergy: 0,
          stepsEnergy: 0,
          waterLogged: 0,
          stepsCount: 0,
        });
      }
    };

    const handleVisibility = () => {
      if (document.visibilityState === 'visible') resetIfNewDay();
    };

    document.addEventListener('visibilitychange', handleVisibility);
    return () => document.removeEventListener('visibilitychange', handleVisibility);
  }, []);

  // ── Streak helpers ─────────────────────────────────────────────────────────
  const markActiveToday = useCallback(() => {
    const today = getTodayKey();
    setStreakData((prev) => {
      if (prev.lastActiveDate === today) return prev; // already counted

      const yesterday = new Date();
      yesterday.setDate(yesterday.getDate() - 1);
      const yesterdayKey = yesterday.toISOString().slice(0, 10);

      const newCount = prev.lastActiveDate === yesterdayKey ? prev.count + 1 : 1;

      if (updateProfile) {
        updateProfile({
          streak: newCount,
          total_workouts: (profile?.total_workouts ?? 0) + 1,
        });
      }

      return { count: newCount, lastActiveDate: today };
    });
  }, [profile, updateProfile]);

  // ── Auto XP helper — awards XP to companion whenever energy is gained ────
  const autoAwardXP = useCallback((energyGained) => {
    if (energyGained <= 0) return;
    const multiplier = calcStreakMultiplier(streakData.count);
    const xpGained = Math.round(energyGained * multiplier);
    setDragonXP((prev) => prev + xpGained);
  }, [streakData.count]);

  // ── Exercise ───────────────────────────────────────────────────────────────
  const addExerciseEnergy = useCallback((durationMin, intensity = 'moderate', reps = 0) => {
    setDailyEnergy((prev) => {
      const multiplier = calcStreakMultiplier(streakData.count);
      const gained = calcExerciseEnergy(durationMin, intensity, reps, multiplier);
      const next = Math.min(MAX_EXERCISE_ENERGY, prev.exerciseEnergy + gained);
      const actualGain = next - prev.exerciseEnergy;
      if (actualGain > 0) autoAwardXP(actualGain);
      return { ...prev, exerciseEnergy: next };
    });
    markActiveToday();
  }, [streakData.count, markActiveToday, autoAwardXP]);

  // ── Sleep ──────────────────────────────────────────────────────────────────
  const logSleep = useCallback((hours) => {
    const energy = calcSleepEnergy(hours);
    setDailyEnergy((prev) => {
      const gain = energy - prev.sleepEnergy;
      if (gain > 0) autoAwardXP(gain);
      return { ...prev, sleepEnergy: energy };
    });
  }, [autoAwardXP]);

  // ── Hydration ──────────────────────────────────────────────────────────────
  const logWater = useCallback((ml) => {
    const weightKg = resolveWeight();
    setDailyEnergy((prev) => {
      const newTotal = prev.waterLogged + ml;
      const energy = calcHydrationEnergy(newTotal, weightKg);
      const gain = energy - prev.hydrationEnergy;
      if (gain > 0) autoAwardXP(gain);
      return { ...prev, waterLogged: newTotal, hydrationEnergy: energy };
    });
  }, [resolveWeight, autoAwardXP]);

  // ── Steps ──────────────────────────────────────────────────────────────────
  const updateSteps = useCallback((count) => {
    const energy = calcStepsEnergy(count);
    setDailyEnergy((prev) => {
      const gain = energy - prev.stepsEnergy;
      if (gain > 0) autoAwardXP(gain);
      return { ...prev, stepsCount: count, stepsEnergy: energy };
    });
  }, [autoAwardXP]);

  // ── Dragon XP feed ─────────────────────────────────────────────────────────
  const feedDragon = useCallback((energy) => {
    const multiplier = calcStreakMultiplier(streakData.count);
    const xpGained = energy * multiplier;
    setDragonXP((prev) => prev + xpGained);
  }, [streakData.count]);

  // ── Derived values ─────────────────────────────────────────────────────────
  const streak = streakData.count;
  const streakMultiplier = calcStreakMultiplier(streak);

  const { exerciseEnergy, sleepEnergy, hydrationEnergy, stepsEnergy, waterLogged, stepsCount } = dailyEnergy;

  const totalEnergy = exerciseEnergy + sleepEnergy + hydrationEnergy + stepsEnergy;
  const totalEnergyPercent = (totalEnergy / MAX_TOTAL_ENERGY) * 100;
  const isPerfectDay = totalEnergy >= PERFECT_DAY_THRESHOLD;

  const weightKg = resolveWeight();
  const waterTarget = weightKg * 35;

  const recoveryScore = calcRecoveryScore(sleepEnergy, hydrationEnergy);
  const recoveryStatus = getRecoveryStatus(recoveryScore);

  const dragonInfo = getDragonInfo(dragonXP);
  const lastResetDate = loadJSON(STORAGE_KEYS.energy)?.date ?? getTodayKey();

  // Activity stats consumed by Home dashboard.
  // weeklyWorkouts: real count from persisted weekly counter (0 if none yet).
  // weeklyWorkoutGoal: honest default target. badgesEarned: 0 until a real
  // achievements source exists (no fabricated constant).
  const weeklyWorkouts = loadWeeklyWorkouts();
  const weeklyWorkoutGoal = WEEKLY_WORKOUT_GOAL;
  const badgesEarned = 0;

  const value = {
    // Exercise
    exerciseEnergy,
    maxExerciseEnergy: MAX_EXERCISE_ENERGY,
    addExerciseEnergy,

    // Sleep
    sleepEnergy,
    maxSleepEnergy: MAX_SLEEP_ENERGY,
    logSleep,

    // Hydration
    hydrationEnergy,
    maxHydrationEnergy: MAX_HYDRATION_ENERGY,
    logWater,
    waterLogged,
    waterTarget,

    // Steps
    stepsEnergy,
    maxStepsEnergy: MAX_STEPS_ENERGY,
    updateSteps,
    stepsCount,

    // Totals
    totalEnergy,
    maxTotalEnergy: MAX_TOTAL_ENERGY,
    totalEnergyPercent,

    // Dragon
    dragonXP,
    dragonLevel: dragonInfo.dragonLevel,
    dragonName: dragonInfo.dragonName,
    xpToNextLevel: dragonInfo.xpToNextLevel,
    xpProgress: dragonInfo.xpProgress,
    feedDragon,

    // Streak
    streak,
    streakMultiplier,

    // Activity stats (Home dashboard)
    weeklyWorkouts,
    weeklyWorkoutGoal,
    badgesEarned,

    // Recovery
    recoveryScore,
    recoveryStatus,

    // Daily flags
    isPerfectDay,
    lastResetDate,
  };

  return (
    <EnergyContext.Provider value={value}>
      {children}
    </EnergyContext.Provider>
  );
}

export function useEnergy() {
  const context = useContext(EnergyContext);
  if (!context) {
    throw new Error('useEnergy must be used within EnergyProvider');
  }
  return context;
}

export default EnergyContext;
